import { BudgetBuilderGroup, BudgetOutcome } from './budgetBuilderContract';
import { outcomeLabel } from './budgetBuilderFormat';
import {
  derivedEfficiency,
  pickSeries,
  resolveIndex,
  sampleIndices,
  scenarioRows
} from './budgetBuilderMath';

type Row = Record<string, string | number>;

const round = (v: number | null, nd = 2): number | '' => (v == null ? '' : Number(v.toFixed(nd)));
const ciLow = (a: number | null, b: number | null): number | '' =>
  a == null || b == null ? '' : Number(Math.min(a, b).toFixed(4));
const ciHigh = (a: number | null, b: number | null): number | '' =>
  a == null || b == null ? '' : Number(Math.max(a, b).toFixed(4));

const baseCols = (g: BudgetBuilderGroup): Row => ({
  Channel: g.channel,
  Platform: g.platform,
  Tactic: g.tactic,
  KPI: g.kpi
});

const pointCols = (g: BudgetBuilderGroup, outcome: BudgetOutcome, index: number): Row => {
  const series = pickSeries(g, outcome);
  const p = series.curve[index];
  const eff = derivedEfficiency(series.efficiencyMetric, p);
  return {
    Outcome: outcomeLabel(series.outcome, g.kpi),
    'Weekly Spend': round(p.spend),
    'Weekly Outcome': round(p.volume),
    'Outcome CI Low': ciLow(p.volumeLower, p.volumeUpper),
    'Outcome CI High': ciHigh(p.volumeLower, p.volumeUpper),
    'Efficiency Metric': series.efficiencyMetric,
    'Weekly Efficiency': round(eff.value, 4),
    'Efficiency CI Low': ciLow(eff.lower, eff.upper),
    'Efficiency CI High': ciHigh(eff.lower, eff.upper)
  };
};

/** Low / selected / high scenario rows per group (matches the on-screen table). */
export const buildSummaryRows = (
  groups: BudgetBuilderGroup[],
  outcome: BudgetOutcome,
  selection: Record<string, number>
): Row[] =>
  groups.flatMap(g => {
    const series = pickSeries(g, outcome);
    const sel = resolveIndex(selection, g.id, series.curve.length);
    return scenarioRows(series.curve.length, sel).map(s => ({
      ...baseCols(g),
      Scenario: s.label,
      ...pointCols(g, outcome, s.index)
    }));
  });

/** Every group's curve, down-sampled at even spend intervals so the file stays small. */
export const buildFullRows = (
  groups: BudgetBuilderGroup[],
  outcome: BudgetOutcome,
  maxPointsPerGroup = 25
): Row[] =>
  groups.flatMap(g => {
    const series = pickSeries(g, outcome);
    return sampleIndices(series.curve.length, maxPointsPerGroup).map(i => ({
      ...baseCols(g),
      ...pointCols(g, outcome, i)
    }));
  });
